import { merge } from 'lodash';
import { CustomAnyOfField } from '@lifepowr/components/src/utils/custom-forms-components';
import type { Dictionary } from './types';
import type { UiSchema } from '@rjsf/utils';

const hidden = () => {
  return <div />;
};

export class UiSchemaGenerator {
  private allowedConfigurationFields: any = {
    'ui:options': {
      label: false,
    },
    address: {
      'ui:options': {
        classNames: 'address-container',
        title: false,
      },
    },
    addressObj: {
      'ui:options': {
        classNames: 'address-container',
        title: false,
      },
    },
    converter: {
      'ui:options': {
        title: '',
      },
      brand: {
        'ui:widget': 'hidden',
      },
      model: {
        'ui:title': 'Converter Model',
      },
    },
  };

  private allowedSettingsFields: any = {
    'ui:title': '',
    'ui:order': [
      // Converter, battery, hybrid PV
      'converters',

      // Retrofit PV
      'pvArraysRetrofit',
      'retrofitPVMeasurement',
      'useUnbiasedEstimators',

      // Tariffs
      'countryStructure',
      'tariffStructure',
      'customInjectionTariffStructure',
      'doubleTariffVariant',
      'VAT',
      'region',
      'dso',

      // Installation, meters, ...
      'hasDigitalMeter',
      'hasP1',
      'hasEastronMeter',
      'isConverterSelfUseCapable',
      'meterCurrentLimitation',
      'ACPhaseCount',
      'injectionLimit',
      'minimumMonthPeak',

      // Battery extra settings
      'minimumStateOfCharge',
      'maximumStateOfCharge',
      'maximumReserveStateOfCharge',

      // The rest
      '*',
    ],
    ACPhaseCount: {
      //"ui:widget": "range",
    },
    hasP1: {},
    hasEastronMeter: {},
    isConverterSelfUseCapable: {
      'ui:title': 'The inverter has a meter and can work standalone',
      'ui:description': 'This installation can work safely and effectively without the EMS',
    },
    converters: {
      'ui:title': 'Converter',
      'ui:options': {
        removable: false,
      },
      items: {
        pvArrays: {
          'ui:title': 'PV Panels Hybrid',
          'ui:field': hidden,
          'ui:description': "Information of each installed group of solar panels ('+' adds; '-' removes)",
          items: {
            solarPanelTilt: {
              'ui:description': 'Horizontal=0, Vertical=90, any corresponding value between 0 and 90',
            },
            solarPanelOrientation: {
              'ui:description': 'E=-90, SE=-45, S=0, SW=45, W=90, or any corresponding value between -180 and 180',
            },
          },
        },
        battery: {
          'ui:title': 'Battery',
          batteryDescription: {},
          packCount: {
            // "ui:widget": "range"
          },
          cellsPerPack: {
            // "ui:widget": "range"
          },
          cellCapacity: {
            // "ui:widget": "range"
          },
          batteryType: {
            'ui:widget': 'hidden',
          },
          cellMinVoltage: {
            'ui:widget': 'hidden',
          },
          cellRatedVoltage: {
            'ui:title': 'Cell Nominal Voltage',
            // "ui:widget": "hidden"
          },
          cellMaxVoltage: {
            'ui:widget': 'hidden',
          },
          cellMaxCurrent: {
            // "ui:widget": "hidden"
          },
          balancingCurrent: {
            'ui:widget': 'hidden',
          },
        },
        curtailmentType: {
          'ui:disabled': true,
        },
        maxACDCPower: {
          'ui:widget': 'hidden',
        },
        maxBatteryCurrent: {
          'ui:widget': 'hidden',
        },
        maxBatteryChargePower: {
          'ui:widget': 'hidden',
        },
        maxBatteryDischargePower: {
          'ui:widget': 'hidden',
        },
        maxMPPTPower: {
          'ui:widget': 'hidden',
        },
      },
    },
    meterCurrentLimitation: {},
    pvArraysRetrofit: {
      'ui:title': 'PV Panels Retrofit',
      'ui:description': "Add existing groups of solar installation(s) ('+' adds; '-' removes)",
      items: {
        solarPanelTilt: {
          'ui:description': 'Horizontal=0, Vertical=90, any corresponding value between 0 and 90',
        },
        solarPanelOrientation: {
          'ui:description': 'E=-90, SE=-45, S=0, SW=45, W=90, or any corresponding value between -180 and 180',
        },
      },
    },
    retrofitPVMeasurement: {
      'ui:enumNames': ['None', 'InverterPvRetro', 'EastronPvRetro'],
      'ui:options': {
        description: (
          <div>
            <div>Select InverterPVRetro if you know for sure PV retrofit data is available.</div>
            <div>Select EastronPVRetro only if HomeWizard Eastron is installed</div>
          </div>
        ),
      },
    },
    tariffStructure: {
      'ui:title': 'Tariff',
      tariffType: {
        'ui:widget': 'hidden',
      },
    },
    useUnbiasedEstimators: {
      'ui:options': {
        description: false,
        help: 'Enable when both Hybrid as Retrofit inverters are present and Retrofit PV is not measured',
      },
    },
    countryStructure: {},
  };

  private createNestedField(fieldNameToHide: string, padNestedForms: boolean, nestedFields?: Dictionary<any>) {
    const fieldUiSchema: any = {
      'ui:label': false,
      'ui:classNames': padNestedForms ? 'nested-form' : '',
    };

    fieldUiSchema[fieldNameToHide] = {
      'ui:widget': 'hidden',
    };

    if (nestedFields) {
      merge(fieldUiSchema, nestedFields);
    }

    return fieldUiSchema;
  }

  private generateMultipleOneOfFields(numberOfFields: number, generatorFn: Function) {
    return Array.from({ length: numberOfFields }, () => generatorFn());
  }

  private generateCustomInjectionTariffFields(numberOfFields: number): Dictionary<any> {
    return {
      'ui:title': 'Specific tariff for injection',
      items: {
        'ui:fieldReplacesAnyOrOneOf': true,
        'ui:field': CustomAnyOfField,
        'ui:title': 'Tariff Name',
        anyOf: this.generateMultipleOneOfFields(numberOfFields, () => this.createNestedField('tariffType', false)),
      },
    };
  }

  /**
   * Country structure fields contain nested oneOf fields that need matching
   * uiSchema fields in order to hide duplicate labels.
   */
  public getCountryStructureUiSchemaFields(
    isWrappedInsideBox: boolean,
    disableDynamicTariffMtuEditing: boolean,
    padNestedForms: boolean = true,
  ): Dictionary<any> {
    const tariffTypeRules = disableDynamicTariffMtuEditing
      ? {
          dynamicTariffType: { 'ui:disabled': true },
        }
      : undefined;

    const customInjectionTariffStructure = {
      'ui:title': 'Custom Injection Tariff',
      items: {
        'ui:title': false,
        'ui:fieldReplacesAnyOrOneOf': true,
        'ui:field': CustomAnyOfField,
        anyOf: this.generateMultipleOneOfFields(this.numberOfTariffFields, () => this.createNestedField('tariffType', padNestedForms, tariffTypeRules)),
      },
    };

    const tariffStructure = {
      'ui:description': false,
      'ui:fieldReplacesAnyOrOneOf': true,
      'ui:field': CustomAnyOfField,
      'ui:title': 'Tariff Name',
      oneOf: this.generateMultipleOneOfFields(this.numberOfTariffFields, () => this.createNestedField('tariffType', padNestedForms, tariffTypeRules)),
    };

    const dsoTariff = {
      'ui:widget': 'hidden',
    };

    const segmentStructure = {
      'ui:description': false,
      'ui:fieldReplacesAnyOrOneOf': true,
      'ui:field': CustomAnyOfField,
      oneOf: this.generateMultipleOneOfFields(3, () =>
        this.createNestedField('segmentName', padNestedForms, {
          tariffStructure,
          customInjectionTariffStructure,
          dsoTariff,
          'ui:title': 'Low voltage',
          'ui:order': ['segmentName', 'dso', 'dsoTariffDropdown', '*'],
        }),
      ),
    };

    const regionStructure = {
      'ui:description': false,
      'ui:fieldReplacesAnyOrOneOf': true,
      'ui:field': CustomAnyOfField,
      oneOf: this.generateMultipleOneOfFields(3, () => this.createNestedField('regionName', padNestedForms, { segmentStructure })),
    };

    return {
      'ui:fieldReplacesAnyOrOneOf': true,
      'ui:field': CustomAnyOfField,
      'ui:title': 'Tariff selection',
      'ui:boxed': isWrappedInsideBox,
      oneOf: [
        // Belgium country structure
        this.createNestedField('countryName', padNestedForms, { regionStructure: regionStructure }),

        // Netherlands country structure
        this.createNestedField('countryName', padNestedForms, { segmentStructure }),

        // None country structure
        this.createNestedField('countryName', padNestedForms, { tariffStructure }),
      ],
    };
  }

  constructor(
    isConverterPvPanelsVisible: boolean, //
    isSettingsOrdered: boolean,
    isConverterWidgetVisible: boolean,
    isConverterSettingsVisible: boolean,
    numberOfCustomInjectionTariffFields: number,
    private readonly numberOfTariffFields: number,
  ) {
    if (isConverterPvPanelsVisible) {
      delete this.allowedSettingsFields.converters.items.pvArrays['ui:field'];
    }

    if (!isSettingsOrdered) {
      delete this.allowedSettingsFields['ui:order'];
    }

    if (isConverterWidgetVisible) {
      delete this.allowedSettingsFields.converters['ui:field'];
      this.allowedConfigurationFields.converter.model = hidden;
    }

    if (isConverterSettingsVisible) {
      const converterFieldsToMakeVisible = [
        'curtailmentType',
        'maxACDCPower',
        'maxBatteryCurrent',
        'maxBatteryChargePower',
        'maxBatteryDischargePower',
        'maxMPPTPower',
      ];

      converterFieldsToMakeVisible.forEach((x) => {
        delete this.allowedSettingsFields.converters.items[x]['ui:widget'];
      });
    }

    const countryStructure = this.getCountryStructureUiSchemaFields(true, false);
    merge(this.allowedSettingsFields.countryStructure, countryStructure);

    if (numberOfCustomInjectionTariffFields > 0) {
      this.allowedSettingsFields.customInjectionTariffStructure = this.generateCustomInjectionTariffFields(numberOfCustomInjectionTariffFields);
    }
  }

  static DeviceSettingsBuilder = class {
    private isConverterPvPanelsVisible = false;
    private isSettingsOrdered = true;
    private isConverterWidgetVisible = false;
    private isConverterSettingsVisible = false;
    private numberOfCustomInjectionTariffFields = 0;
    private numberOfTariffFields = 50;

    setConverterPvPanelsVisible(isVisible: boolean) {
      this.isConverterPvPanelsVisible = isVisible;
      return this;
    }

    setSettingsOrdered(isOrdered: boolean) {
      this.isSettingsOrdered = isOrdered;
      return this;
    }

    setConverterWidgetVisible(isVisible: boolean) {
      this.isConverterWidgetVisible = isVisible;
      return this;
    }

    setConverterSettingsVisible(isVisible: boolean) {
      this.isConverterSettingsVisible = isVisible;
      return this;
    }

    setNumberOfCustomInjectionTariffFields(numberOfFields: number) {
      this.numberOfCustomInjectionTariffFields = numberOfFields;
      return this;
    }

    setMaxTariffLength(maxTariffLength: number) {
      this.numberOfTariffFields = maxTariffLength;
      return this;
    }

    build() {
      const uiSchema = new UiSchemaGenerator(
        this.isConverterPvPanelsVisible,
        this.isSettingsOrdered,
        this.isConverterWidgetVisible,
        this.isConverterSettingsVisible,
        this.numberOfCustomInjectionTariffFields,
        this.numberOfTariffFields,
      );

      return {
        configuration: {
          ...uiSchema.allowedConfigurationFields,
        },
        settings: {
          ...uiSchema.allowedSettingsFields,
        },
      };
    }
  };

  static TariffStructureBuilder = class {
    private padNestedForms = true;
    private numberOfTariffFields = 50;

    public setPadNestedForms(isPadded: boolean) {
      this.padNestedForms = isPadded;
      return this;
    }

    setMaxTariffLength(maxTariffLength: number) {
      this.numberOfTariffFields = maxTariffLength;
      return this;
    }

    public build(): UiSchema {
      const uiSchemaGenerator = new UiSchemaGenerator(
        false, //
        false,
        false,
        false,
        0,
        this.numberOfTariffFields,
      );

      return uiSchemaGenerator.getCountryStructureUiSchemaFields(false, false, this.padNestedForms);
    }
  };
}

export const DEFAULT_SCHEMA_KEYS = [
  'tariffStructure',
  'customInjectionTariffStructure',
  'VAT',
  'dso',
  'hasDigitalMeter',
  'minimumStateOfCharge',
  'maximumStateOfCharge',
  'maximumReserveStateOfCharge',
  'latitude',
  'longitude',
  'injectionLimit',
  'meterPowerDeadband',
  'hasP1',
  'hasEastronMeter',
  'meterCurrentLimitation',
  'ACPhaseCount',
  'genericLoadMaximumElectricityPrice',
  'isConverterSelfUseCapable',
  'retrofitPVMeasurement',
  'doubleTariffVariant',
  'enableThreePhaseLoadBalancing',
  'useUnbiasedEstimators',
  'pvArraysRetrofit',
  'converters',
  'minimumMonthPeak',

  'countryStructure',
];

export const USER_NONE_ADVANCED_SCHEMA_KEYS = ['tariffStructure', 'countryStructure'];

/**
 * The function filters out schema fields based on a given array of keys to keep.
 * @returns a schema object that contains only the fields specified in the `keysToKeep` array.
 */
export function filterOutSchemaFields({ properties = {} }, keysToKeep: string[]) {
  return Object.fromEntries(Object.entries(properties).filter(([key]) => keysToKeep.includes(key)));
}
